import React, { useState } from "react";
import api from "@/lib/api";
import { useI18n } from "@/i18n";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Loader2, Copy, Sparkles, PenLine } from "lucide-react";

const OBJECTIVES = ["WhatsApp Leads", "Website Leads", "Sales", "Booking", "Awareness", "Retargeting"];
const TONES = ["confident", "friendly", "urgent", "premium", "playful"];

export default function Copywriter() {
  const { lang } = useI18n();
  const [form, setForm] = useState({ product: "", objective: "WhatsApp Leads", audience: "", offer: "", tone: "confident", language: lang === "bm" ? "Malay" : "English" });
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const generate = async () => {
    if (!form.product) { toast.error("Enter a product first"); return; }
    setLoading(true); setResult(null);
    try { const r = await api.post("/ai/copywriter", form); setResult(r.data); toast.success("Copy generated"); }
    catch (e) { toast.error("Generation failed"); }
    finally { setLoading(false); }
  };

  const copy = (text) => { navigator.clipboard.writeText(text); toast.success("Copied to clipboard"); };

  return (
    <div className="grid lg:grid-cols-5 gap-6 animate-float-up" data-testid="copywriter-panel">
      <div className="lg:col-span-2 bg-card border border-border rounded-2xl p-6 space-y-4 h-fit">
        <div className="space-y-2"><Label>Product / Service</Label><Input data-testid="copy-product" placeholder="e.g. Glow Serum Bundle" value={form.product} onChange={(e) => set("product", e.target.value)} /></div>
        <div className="space-y-2"><Label>Objective</Label>
          <Select value={form.objective} onValueChange={(v) => set("objective", v)}><SelectTrigger data-testid="copy-objective"><SelectValue /></SelectTrigger><SelectContent>{OBJECTIVES.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent></Select>
        </div>
        <div className="space-y-2"><Label>Audience</Label><Input data-testid="copy-audience" placeholder="e.g. Women 25-40, skincare lovers" value={form.audience} onChange={(e) => set("audience", e.target.value)} /></div>
        <div className="space-y-2"><Label>Offer</Label><Input data-testid="copy-offer" placeholder="e.g. Buy 2 Free 1" value={form.offer} onChange={(e) => set("offer", e.target.value)} /></div>
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-2"><Label>Tone</Label>
            <Select value={form.tone} onValueChange={(v) => set("tone", v)}><SelectTrigger data-testid="copy-tone"><SelectValue /></SelectTrigger><SelectContent>{TONES.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent></Select>
          </div>
          <div className="space-y-2"><Label>Language</Label>
            <Select value={form.language} onValueChange={(v) => set("language", v)}><SelectTrigger data-testid="copy-language"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="English">English</SelectItem><SelectItem value="Malay">Malay</SelectItem></SelectContent></Select>
          </div>
        </div>
        <Button data-testid="copy-generate" onClick={generate} disabled={loading} className="w-full bg-primary hover:bg-primary/90 text-primary-foreground font-semibold rounded-xl">
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Sparkles className="w-4 h-4 mr-1" /> Generate Copy</>}
        </Button>
      </div>

      <div className="lg:col-span-3 space-y-4">
        {!result && !loading && <div className="bg-card/40 border border-dashed border-border rounded-2xl p-12 text-center text-muted-foreground"><PenLine className="w-8 h-8 mx-auto mb-3 opacity-50" />Fill the form and generate multiple ad copy variants.</div>}
        {loading && <div className="flex flex-col items-center py-16 gap-3"><Loader2 className="w-7 h-7 animate-spin text-primary" /><p className="text-sm text-muted-foreground">Writing copy variants…</p></div>}
        {result?.variants?.map((v, i) => (
          <div key={i} data-testid={`copy-variant-${i}`} className="bg-card border border-border rounded-2xl p-5">
            <div className="flex items-center justify-between mb-3">
              <span className="text-xs font-mono uppercase tracking-widest text-primary">Variant {i + 1}</span>
              <button data-testid={`copy-variant-copy-${i}`} onClick={() => copy(`${v.primary_text}\n\n${v.headline}\n${v.description}\n${v.cta}`)} className="text-muted-foreground hover:text-primary transition-colors"><Copy className="w-4 h-4" /></button>
            </div>
            <p className="text-sm mb-3">{v.primary_text}</p>
            <p className="font-display font-semibold">{v.headline}</p>
            <p className="text-sm text-muted-foreground">{v.description}</p>
            <span className="inline-block mt-3 text-xs font-semibold px-3 py-1 rounded-full bg-primary/15 text-primary">{v.cta}</span>
          </div>
        ))}
        {result?.image_brief && (
          <div className="bg-secondary/30 border border-border rounded-2xl p-5">
            <p className="text-xs font-mono uppercase tracking-widest text-accent mb-2">Image Brief</p>
            <p className="text-sm text-muted-foreground">{result.image_brief}</p>
            {result.video_script && <><p className="text-xs font-mono uppercase tracking-widest text-accent mt-4 mb-2">Video Script</p><p className="text-sm text-muted-foreground whitespace-pre-line">{result.video_script}</p></>}
          </div>
        )}
      </div>
    </div>
  );
}
